import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FranceMap from '@svg-maps/france.regions';

const regionMeta = {
  ara: { label: 'Auvergne-Rhone-Alpes', lat: 45.4, lng: 4.4, zoom: 8 },
  bfc: { label: 'Bourgogne-Franche-Comte', lat: 47.0, lng: 5.5, zoom: 8 },
  bre: { label: 'Bretagne', lat: 48.2, lng: -2.8, zoom: 8 },
  cvl: { label: 'Centre-Val de Loire', lat: 47.5, lng: 1.7, zoom: 8 },
  cor: { label: 'Corse', lat: 42.15, lng: 9.1, zoom: 9 },
  ges: { label: 'Grand Est', lat: 48.6, lng: 5.8, zoom: 8 },
  hdf: { label: 'Hauts-de-France', lat: 49.9, lng: 2.8, zoom: 8 },
  idf: { label: 'Ile-de-France', lat: 48.85, lng: 2.35, zoom: 10 },
  nor: { label: 'Normandie', lat: 48.9, lng: 0.2, zoom: 8 },
  naq: { label: 'Nouvelle-Aquitaine', lat: 45.2, lng: 0.0, zoom: 7 },
  occ: { label: 'Occitanie', lat: 43.6, lng: 2.3, zoom: 8 },
  pdl: { label: 'Pays de la Loire', lat: 47.5, lng: -0.8, zoom: 8 },
  pac: { label: "Provence-Alpes-Cote d'Azur", lat: 43.9, lng: 6.1, zoom: 8 },
  // DOM-TOM
  gua: { label: 'Guadeloupe', lat: 16.25, lng: -61.55, zoom: 10 },
  mtq: { label: 'Martinique', lat: 14.64, lng: -61.02, zoom: 10 },
  guy: { label: 'Guyane', lat: 3.93, lng: -53.13, zoom: 7 },
  reu: { label: 'La Reunion', lat: -21.11, lng: 55.53, zoom: 10 },
  may: { label: 'Mayotte', lat: -12.78, lng: 45.15, zoom: 11 },
};

// Simplified DOM-TOM shapes as SVG insets
const domTomTerritories = [
  {
    id: 'gua',
    label: 'Guadeloupe',
    // Butterfly shape of Grande-Terre and Basse-Terre
    path: 'M8,18 L12,14 L18,12 L22,14 L24,12 L22,8 L18,6 L14,8 L12,6 L8,8 L6,12 L8,18 Z M26,16 L30,12 L34,10 L38,12 L36,18 L32,20 L28,18 L26,16 Z',
    box: { x: 15, y: 600, w: 80, h: 55 },
  },
  {
    id: 'mtq',
    label: 'Martinique',
    path: 'M16,6 L20,4 L24,6 L26,10 L24,16 L22,22 L18,28 L14,26 L12,20 L14,14 L16,10 L16,6 Z',
    box: { x: 105, y: 600, w: 80, h: 55 },
  },
  {
    id: 'guy',
    label: 'Guyane',
    path: 'M6,28 L10,24 L16,22 L22,16 L28,10 L34,6 L38,8 L36,14 L38,20 L34,26 L30,30 L24,32 L18,34 L12,32 L8,30 L6,28 Z',
    box: { x: 195, y: 600, w: 80, h: 55 },
  },
  {
    id: 'reu',
    label: 'La Reunion',
    path: 'M10,16 L14,10 L20,8 L26,10 L30,14 L32,20 L30,26 L26,30 L20,32 L14,30 L10,26 L8,20 L10,16 Z',
    box: { x: 285, y: 600, w: 80, h: 55 },
  },
  {
    id: 'may',
    label: 'Mayotte',
    path: 'M12,8 L18,4 L24,6 L28,12 L26,20 L22,26 L16,28 L12,24 L10,18 L12,12 L12,8 Z M30,14 L34,12 L36,16 L34,20 L30,18 L30,14 Z',
    box: { x: 375, y: 600, w: 80, h: 55 },
  },
];

const FranceRegionsMap = ({ eventCountByRegion = {} }) => {
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleClick = (regionId) => {
    const meta = regionMeta[regionId];
    if (meta) {
      navigate(`/events?view=map&lat=${meta.lat}&lng=${meta.lng}&zoom=${meta.zoom}&region=${encodeURIComponent(meta.label)}`);
    }
  };

  const hoveredMeta = hoveredRegion ? regionMeta[hoveredRegion] : null;
  const hoveredCount = hoveredRegion ? (eventCountByRegion[hoveredRegion] || 0) : 0;

  const renderRegionPath = (id, path, isMetro = true) => {
    const isHovered = hoveredRegion === id;
    const hasEvents = (eventCountByRegion[id] || 0) > 0;
    return (
      <path
        key={id}
        id={id}
        d={path}
        fill={isHovered ? '#f97316' : hasEvents ? '#334155' : '#475569'}
        stroke={isHovered ? '#fb923c' : '#1e293b'}
        strokeWidth={isHovered ? '2' : '1'}
        className="transition-all duration-300 ease-out cursor-pointer"
        style={{
          filter: isHovered ? 'url(#glow)' : 'none',
          opacity: hoveredRegion && !isHovered ? 0.5 : 1,
        }}
        onMouseEnter={() => setHoveredRegion(id)}
        onMouseLeave={() => setHoveredRegion(null)}
        onClick={() => handleClick(id)}
        data-testid={`region-${id}`}
      />
    );
  };

  return (
    <div className="relative w-full max-w-3xl mx-auto" data-testid="france-regions-map">
      {/* Tooltip */}
      {hoveredMeta && (
        <div
          className="pointer-events-none absolute z-20 bg-asphalt text-white px-4 py-2.5 shadow-xl border border-brand/30 transition-opacity duration-150"
          style={{
            left: `${mousePos.x}px`,
            top: `${mousePos.y - 60}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <p className="font-heading font-bold text-sm uppercase tracking-wider">{hoveredMeta.label}</p>
          <p className="text-brand text-xs font-heading font-bold mt-0.5">
            {hoveredCount > 0 ? `${hoveredCount} ${t('map.events')}` : t('map.exploreRegion')}
          </p>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-asphalt" />
        </div>
      )}

      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="15 -1 598 680"
        className="w-full h-auto cursor-pointer"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
          });
        }}
      >
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Metropolitan France regions */}
        {FranceMap.locations.map((location) => renderRegionPath(location.id, location.path))}

        {/* DOM-TOM insets */}
        {domTomTerritories.map((territory) => {
          const isHovered = hoveredRegion === territory.id;
          const { x, y, w, h } = territory.box;

          return (
            <g key={territory.id}>
              {/* Inset box */}
              <rect
                x={x} y={y} width={w} height={h}
                rx="3" ry="3"
                fill={isHovered ? 'rgba(249,115,22,0.1)' : 'rgba(255,255,255,0.04)'}
                stroke={isHovered ? '#f97316' : 'rgba(255,255,255,0.15)'}
                strokeWidth="1"
                className="transition-all duration-300"
              />
              {/* Territory shape */}
              <g transform={`translate(${x + w / 2 - 20}, ${y + 4})`}>
                {renderRegionPath(territory.id, territory.path, false)}
              </g>
              {/* Label */}
              <text
                x={x + w / 2}
                y={y + h - 5}
                textAnchor="middle"
                fill={isHovered ? '#f97316' : 'rgba(255,255,255,0.6)'}
                fontSize="8"
                fontFamily="'Barlow Condensed', sans-serif"
                fontWeight="700"
                letterSpacing="0.5"
                className="uppercase transition-all duration-300 pointer-events-none"
              >
                {territory.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default FranceRegionsMap;
